import { useEffect, useRef, useState } from "react";
import { PATHWAY } from "@/constants/testIds";
import { useRuntime } from "@/lib/RuntimeContext";

function fmt(t) {
  if (!t) return "—";
  try {
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "—";
  }
}

// Canonical KATLAS status vocabulary. Distinct labels per baseline §7.
const STATUS_META = {
  active: {
    label: "Active",
    dot: "bg-amber-500",
    text: "text-amber-500",
    pulse: false,
  },
  simulation: {
    label: "Simulation",
    dot: "bg-neutral-400",
    text: "text-neutral-300",
    pulse: false,
  },
  unconfigured: {
    label: "Unconfigured",
    dot: "bg-[#dc2626]",
    text: "text-[#dc2626]",
    pulse: true,
  },
  auth_rejected: {
    label: "Auth rejected",
    dot: "bg-[#dc2626]",
    text: "text-[#dc2626]",
    pulse: true,
  },
  unreachable: {
    label: "Unreachable",
    dot: "bg-[#dc2626]",
    text: "text-[#dc2626]",
    pulse: true,
  },
  node_error: {
    label: "Node error",
    dot: "bg-[#dc2626]",
    text: "text-[#dc2626]",
    pulse: true,
  },
};

function metaFor(status) {
  return STATUS_META[status] || STATUS_META.unreachable;
}

export default function RuntimeIndicator() {
  const { status } = useRuntime();
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    function onDown(e) {
      if (!open) return;
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (open && e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const s = status || {};
  const m = metaFor(s.status);

  return (
    <div ref={wrap} className="relative" data-testid={PATHWAY.runtimeIndicator}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={PATHWAY.runtimeIndicatorToggle}
        aria-expanded={open}
        aria-label="KATLAS runtime status"
        className="flex items-center gap-2 border hairline px-3 py-2 transition-colors hover:border-neutral-500"
      >
        <span
          className={`inline-block h-2 w-2 ${m.dot} ${m.pulse ? "animate-pulse" : ""}`}
        />
        <span className="hidden font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-300 sm:inline">
          KATLAS · {m.label}
        </span>
        <span className={`font-mono text-[10px] uppercase tracking-[0.22em] sm:hidden ${m.text}`}>
          {m.label}
        </span>
      </button>

      {open && (
        <div
          data-testid={PATHWAY.runtimeIndicatorPanel}
          className="absolute right-0 top-full z-50 mt-2 w-80 border hairline bg-[#0f0f0f] shadow-2xl"
        >
          <div className="flex items-center justify-between border-b hairline px-4 py-3">
            <div className="flex items-center gap-2">
              <span className={`inline-block h-2 w-2 ${m.dot}`} />
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-300">
                KATLAS runtime
              </span>
            </div>
            <span className={`font-mono text-[10px] uppercase tracking-[0.22em] ${m.text}`}>
              {m.label}
            </span>
          </div>
          <Row label="Status" value={m.label} testId="status" tone={m.text} />
          <Row label="Mode" value={s.mode || "—"} testId="mode" />
          <Row label="Node" value={s.node_id || "—"} testId="node" />
          <Row label="Version" value={s.version || "—"} testId="version" />
          <Row
            label="Signing service"
            value={s.signing_service_available ? "Available" : "Unavailable"}
            testId="signing-service"
          />
          <Row
            label="Health"
            value={s.health ? s.health.replace(/_/g, " ") : "—"}
            testId="health"
          />
          <Row label="Last check" value={fmt(s.last_check)} testId="last-check" />
          <div className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            {s.message || "—"}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, tone: toneClass, testId }) {
  return (
    <div
      data-testid={PATHWAY.runtimeField(testId)}
      className="grid grid-cols-[110px_1fr] items-baseline gap-3 border-b hairline px-4 py-2 last:border-b-0"
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">
        {label}
      </span>
      <span
        className={`font-mono text-[12px] break-words ${
          toneClass || "text-neutral-100"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
